import React, { useState, useRef, useEffect } from "react";
import { BadgeCheck, ChevronDown, ChevronUp } from "lucide-react";

// --- Types ---
type Review = {
  id: number;
  name: string;
  title: string; // e.g. "Age 32"
  rating: number;
  text: string;
  verified: boolean;
  date: string;
  city: string;
};

const REVIEWS_PER_PAGE = 3;

// Major cities across English-speaking countries
const LOCATIONS = [
  "London, UK", "New York, USA", "Los Angeles, USA", "Chicago, USA", "Toronto, Canada",
  "Vancouver, Canada", "Sydney, Australia", "Melbourne, Australia", "Brisbane, Australia",
  "Perth, Australia", "Auckland, New Zealand", "Wellington, New Zealand", "Dublin, Ireland",
  "Belfast, UK", "Edinburgh, UK", "Manchester, UK", "Birmingham, UK", "Cape Town, South Africa",
  "Johannesburg, South Africa", "Singapore",
];

// --- Male names list (choose your preferred list or adjust as needed) ---
const MALE_NAMES = [
  "James", "John", "Robert", "Michael", "William", "David", "Richard", "Joseph", "Thomas", "Charles",
  "Christopher", "Daniel", "Matthew", "Anthony", "Mark", "Donald", "Steven", "Paul", "Andrew", "Joshua",
  "Kevin", "Brian", "George", "Edward", "Ronald", "Timothy", "Jason", "Jeffrey", "Ryan", "Jacob",
  "Gary", "Nicholas", "Eric", "Jonathan", "Stephen", "Larry", "Justin", "Scott", "Brandon", "Benjamin",
  "Samuel", "Frank", "Gregory", "Raymond", "Alexander", "Patrick", "Jack", "Dennis", "Jerry", "Tyler",
  "Aaron", "Jose", "Henry", "Adam", "Douglas", "Nathan", "Peter", "Zachary", "Walter", "Kyle", "Harold",
  "Carl", "Jeremy", "Keith", "Roger", "Gerald", "Ethan", "Arthur", "Terry", "Christian", "Sean",
  "Lawrence", "Austin", "Joe", "Noah", "Jesse", "Albert", "Bryan", "Billy", "Bruce", "Willie",
  "Jordan", "Dylan", "Alan", "Ralph", "Gabriel", "Roy", "Juan", "Wayne", "Eugene", "Logan",
  "Randy", "Louis", "Russell", "Vincent", "Philip", "Bobby", "Johnny", "Bradley", "Martin", "Shawn",
  "Lee", "Isaac", "Cody", "Julian", "Alex", "Evan", "Ian", "Luke", "Nathaniel", "Shane",
  "Cameron", "Connor", "Caleb", "Aidan", "Owen", "Mason", "Hunter", "Cole", "Blake", "Gavin",
  "Chase", "Dominic", "Max", "Liam", "Seth", "Joel", "Micah", "Miles", "Brody", "Jake", "Logan",
  "Toby", "Oscar", "Reece", "Ellis", "Finley", "Jaxon", "Morgan", "Harvey", "Corey", "Kieran",
  "Tom", "Ben", "Lewis", "Jamie", "Sam", "Josh",
];

// --- Realistic review templates ---
const REVIEW_TEXTS = [
  "Finally found something that makes sense. Clear, honest advice instead of empty promises.",
  "Gave me back my confidence. Simple and practical steps I could actually follow.",
  "No more panicking every morning. I feel in control of my hair for the first time.",
  "Loved the science-backed approach. I finally know what actually works.",
  "Stopped wasting money on random treatments. I have a proper plan now.",
  "The personal touch really made a difference. I felt listened to and understood.",
  "My stress about hair loss is gone. It's so much easier to deal with day to day.",
  "Wish I’d done this years ago. My only regret is waiting so long.",
  "The explanations are super clear. No hype or nonsense.",
  "I went from constant worry to having peace of mind.",
  "Simple steps, realistic expectations. No miracle cures, just what actually works.",
  "The best thing I’ve done for my confidence in years.",
  "I finally feel like myself again. Thank you!",
  "The advice is straightforward and no-nonsense. Exactly what I needed.",
  "I recommend this to any guy who feels lost about his hair.",
  "I was embarrassed to talk about it, but this made it normal.",
  "Great mix of science and understanding. I left feeling hopeful.",
  "At 40+, I thought it was too late. Glad to see there are still options.",
  "I stopped doomscrolling hair loss forums. Way less stress now.",
  "Seeing real results without hype. Highly recommend.",
  "For once, I got advice that wasn’t trying to sell me something.",
  "No more guessing games with my hair. I know exactly what to do.",
  "From stressed to sorted. Can’t thank you enough.",
  "It feels good to have a clear, honest plan for once.",
  "Reassuring, practical, and easy to follow. Just what I needed.",
  "I stopped obsessing and started living my life again.",
  "Easy to understand, and it actually works.",
  "The best investment I’ve made for myself this year.",
  "Friends noticed I was less stressed—this is why.",
  "I felt supported every step of the way.",
];

// --- Helper for dates (cycles 2023-2025, more recent first) ---
const DATE_LIST = [
  "December, 2025", "November, 2025", "October, 2025", "September, 2025", "August, 2025", "July, 2025",
  "June, 2025", "May, 2025", "April, 2025", "March, 2025", "February, 2025", "January, 2025",
  "December, 2024", "November, 2024", "October, 2024", "September, 2024", "August, 2024", "July, 2024",
  "June, 2024", "May, 2024", "April, 2024", "March, 2024", "February, 2024", "January, 2024",
  "December, 2023", "November, 2023", "October, 2023", "September, 2023", "August, 2023", "July, 2023",
];

// --- Helper for Initials ---
const getInitial = (name: string) => {
  if (!name) return "?";
  return name.trim()[0].toUpperCase();
};

// --- Generate 146 reviews ---
const generateReviews = (): Review[] => {
  const reviews: Review[] = [];
  let cityIdx = 0;
  let dateIdx = 0;
  let textIdx = 0;

  for (let i = 0; i < 146; i++) {
    const name = MALE_NAMES[i % MALE_NAMES.length] + " " + String.fromCharCode(65 + (i % 18)) + ".";
    const age = 19 + (i % 29); // Age between 19 and 47
    const rating = i % 11 === 0 ? 4 : 5; // Sprinkle a few 4-star reviews
    const text = REVIEW_TEXTS[textIdx];
    const verified = true;
    const date = DATE_LIST[dateIdx];
    const city = LOCATIONS[cityIdx];

    reviews.push({
      id: i + 1,
      name,
      title: `Age ${age}`,
      rating,
      text,
      verified,
      date,
      city,
    });

    // Cycle indexes
    cityIdx = (cityIdx + 1) % LOCATIONS.length;
    dateIdx = (dateIdx + 1) % DATE_LIST.length;
    textIdx = (textIdx + 1) % REVIEW_TEXTS.length;
  }

  return reviews;
};

const REVIEWS = generateReviews();

// --- Component ---
const ReviewsModal: React.FC<{ open: boolean; onClose: () => void }> = ({
  open,
  onClose,
}) => {
  const [page, setPage] = useState(0);
  const [reviewOpen, setReviewOpen] = useState(false);
  const reviewFormRef = useRef<HTMLDivElement>(null);

  const [rating, setRating] = useState<number | null>(null);
  const [text, setText] = useState("");
  const [businessCategory, setBusinessCategory] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [fullName, setFullName] = useState("");

  useEffect(() => {
    if (!open) {
      setPage(0);
      setReviewOpen(false);
      setSubmitted(false);
      setRating(null);
      setText("");
      setFullName("");
      setBusinessCategory("");
    }
  }, [open]);

  useEffect(() => {
    if (reviewOpen && reviewFormRef.current) {
      reviewFormRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [reviewOpen]);

  if (!open) return null;

  const totalPages = Math.ceil(REVIEWS.length / REVIEWS_PER_PAGE);
  const visible = REVIEWS.slice(
    page * REVIEWS_PER_PAGE,
    page * REVIEWS_PER_PAGE + REVIEWS_PER_PAGE
  );

  const handleSubmit = () => {
    if (rating && text.trim() && fullName.trim()) {
      setSubmitted(true);
      setText("");
      setRating(null);
      setFullName("");
      setBusinessCategory("");
      setTimeout(() => setSubmitted(false), 2500);
      setReviewOpen(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center px-2">
      <div
        className={
          `bg-white w-full max-w-xl rounded-2xl shadow-2xl p-6 relative flex h-[90vh] flex-col transition-all duration-300` +
          (reviewOpen ? " h-auto min-h-[90vh]" : " h-[90vh]")
        }
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-[1.875rem] text-gray-400 hover:text-gray-700 focus:outline-none"
          aria-label="Close reviews modal"
        >
          ×
        </button>

        <h2 className="flex items-center gap-2 text-2xl font-bold mb-4 tracking-tight text-gray-800">
          <span>Verified Reviews</span>
          <BadgeCheck
            size={20}
            className="text-blue-400"
            aria-label="Verified reviews"
          />
        </h2>

        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="space-y-5 pb-2">
            {visible.map((r) => (
              <div
                key={r.id}
                className="flex gap-3 items-start bg-gray-50/60 rounded-xl p-3"
              >
                {/* Initials Avatar */}
                <div
                  className={`
                    flex items-center justify-center
                    w-10 h-10 min-w-[2.5rem] min-h-[2.5rem]
                    rounded-full bg-gray-200 text-gray-600
                    font-bold text-lg shadow
                    select-none
                  `}
                  aria-hidden="true"
                >
                  {getInitial(r.name)}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-1 mb-0.5">
                    <span className="font-semibold text-sm text-gray-900">
                      {r.name}
                    </span>
                    {r.verified && (
                      <BadgeCheck
                        size={15}
                        strokeWidth={2.2}
                        className="text-blue-400"
                        aria-label="Verified"
                      />
                    )}
                    <span className="text-xs text-gray-500 ml-2">
                      {r.title}
                    </span>
                  </div>
                  <div className="text-xs text-gray-400 mb-1">
                    {r.date}, {r.city}
                  </div>
                  <div className="text-yellow-500 text-base leading-none mb-0.5">
                    {"★".repeat(r.rating)}
                    <span className="text-gray-300">
                      {r.rating < 5 && "★".repeat(5 - r.rating)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700">{r.text}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-between items-center mt-4">
            <button
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
              className="text-sm font-semibold text-gray-400 hover:text-gray-700 disabled:opacity-30 transition"
            >
              ← Previous
            </button>
            <span className="text-xs text-gray-400 select-none">
              Page {page + 1} of {totalPages}
            </span>
            <button
              disabled={page === totalPages - 1}
              onClick={() => setPage(page + 1)}
              className="text-sm font-semibold text-gray-700 hover:text-green-600 disabled:opacity-30 transition"
            >
              Next →
            </button>
          </div>

          <div className="border-t my-7" />

          {/* Write a review dropdown */}
          <div ref={reviewFormRef}>
            <button
              onClick={() => setReviewOpen((open) => !open)}
              className="flex items-center gap-1 font-semibold mb-3 text-lg text-gray-900 w-full justify-between focus:outline-none"
              aria-expanded={reviewOpen}
              aria-controls="write-review-form"
            >
              Write a review
              {reviewOpen ? (
                <ChevronUp size={20} className="text-gray-500" />
              ) : (
                <ChevronDown size={20} className="text-gray-500" />
              )}
            </button>

            <div
              id="write-review-form"
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                reviewOpen
                  ? "max-h-[500px] opacity-100"
                  : "max-h-0 opacity-0 pointer-events-none"
              }`}
              aria-hidden={!reviewOpen}
            >
              <div className="pt-1 pb-5">
                {!submitted ? (
                  <>
                    {/* Star rating row */}
                    <div className="flex items-center mb-3">
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <button
                            key={s}
                            onClick={() => setRating(s)}
                            className={`text-2xl transition cursor-pointer ${
                              rating && rating >= s
                                ? "text-yellow-500"
                                : "text-gray-300"
                            }`}
                            aria-label={`Rate ${s} star${
                              s > 1 ? "s" : ""
                            }`}
                            type="button"
                          >
                            ★
                          </button>
                        ))}
                      </div>
                    </div>
                    {/* Full Name */}
                    <input
                      type="text"
                      className="w-full border rounded-lg px-3 py-2 text-sm mb-3 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-200 placeholder-gray-400"
                      placeholder="Full name (e.g. Sarah Collins)"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      maxLength={32}
                      aria-label="Full name"
                      autoComplete="name"
                    />
                    {/* Business + Role */}
                    <input
                      type="text"
                      className="w-full border rounded-lg px-3 py-2 text-sm mb-3 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-200 placeholder-gray-400"
                      placeholder="Your city (e.g. Londong)"
                      value={businessCategory}
                      onChange={(e) =>
                        setBusinessCategory(e.target.value)
                      }
                      maxLength={36}
                      aria-label="Business category"
                      autoComplete="off"
                    />
                    {/* Review Text */}
                    <textarea
                      className="w-full border rounded-lg p-2 text-sm mb-3 resize-none focus:outline-none focus:ring-2 focus:ring-green-200"
                      rows={2}
                      placeholder="Write your review..."
                      value={text}
                      maxLength={180}
                      onChange={(e) => setText(e.target.value)}
                      aria-label="Review text"
                    />
                    {/* Submit */}
                    <button
                      disabled={
                        !rating || !text.trim() || !fullName.trim()
                      }
                      onClick={handleSubmit}
                      className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-40 text-white font-semibold py-2 rounded-lg transition"
                    >
                      Submit review
                    </button>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center py-10">
                    <BadgeCheck
                      size={40}
                      className="text-green-500 mb-2"
                    />
                    <div className="text-green-700 text-base font-semibold text-center">
                      Your review has been uploaded
                      <br />
                      and is awaiting approval.
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewsModal;
